'use client';

import { useState, useEffect } from 'react';
import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface LineMovement {
  id: number;
  game: string;
  market: string;
  openLine: number;
  currentLine: number;
  movement: number;
  sharpPct: number;
  publicPct: number;
  velocity: 'fast' | 'moderate' | 'slow';
  signal: 'sharp' | 'public' | 'neutral';
  books: { name: string; line: number }[];
  timeline: { time: string; line: number }[];
  anomaly: boolean;
}

const MOVEMENTS: LineMovement[] = [
  {
    id: 1, game: 'BOS vs TOR', market: 'Moneyline BOS',
    openLine: -145, currentLine: -162, movement: -17,
    sharpPct: 72, publicPct: 28, velocity: 'fast', signal: 'sharp', anomaly: true,
    books: [
      { name: 'DraftKings', line: -162 }, { name: 'FanDuel', line: -158 },
      { name: 'BetMGM', line: -165 }, { name: 'Caesars', line: -160 },
    ],
    timeline: [
      { time: '09:00', line: -145 }, { time: '10:00', line: -148 }, { time: '11:00', line: -152 },
      { time: '12:00', line: -155 }, { time: '13:00', line: -158 }, { time: '14:00', line: -162 },
    ],
  },
  {
    id: 2, game: 'COL vs VGK', market: 'Total O/U 6.0',
    openLine: -110, currentLine: -125, movement: -15,
    sharpPct: 65, publicPct: 35, velocity: 'moderate', signal: 'sharp', anomaly: true,
    books: [
      { name: 'DraftKings', line: -125 }, { name: 'FanDuel', line: -120 },
      { name: 'BetMGM', line: -128 }, { name: 'Caesars', line: -122 },
    ],
    timeline: [
      { time: '09:00', line: -110 }, { time: '10:00', line: -112 }, { time: '11:00', line: -115 },
      { time: '12:00', line: -118 }, { time: '13:00', line: -122 }, { time: '14:00', line: -125 },
    ],
  },
  {
    id: 3, game: 'EDM vs CGY', market: 'Moneyline EDM',
    openLine: -130, currentLine: -118, movement: 12,
    sharpPct: 38, publicPct: 62, velocity: 'slow', signal: 'public', anomaly: false,
    books: [
      { name: 'DraftKings', line: -118 }, { name: 'FanDuel', line: -120 },
      { name: 'BetMGM', line: -115 }, { name: 'Caesars', line: -122 },
    ],
    timeline: [
      { time: '09:00', line: -130 }, { time: '10:00', line: -128 }, { time: '11:00', line: -125 },
      { time: '12:00', line: -122 }, { time: '13:00', line: -120 }, { time: '14:00', line: -118 },
    ],
  },
  {
    id: 4, game: 'NYR vs PHI', market: 'Puck Line NYR -1.5',
    openLine: 145, currentLine: 128, movement: -17,
    sharpPct: 68, publicPct: 32, velocity: 'fast', signal: 'sharp', anomaly: true,
    books: [
      { name: 'DraftKings', line: 128 }, { name: 'FanDuel', line: 132 },
      { name: 'BetMGM', line: 125 }, { name: 'Caesars', line: 130 },
    ],
    timeline: [
      { time: '09:00', line: 145 }, { time: '10:00', line: 142 }, { time: '11:00', line: 138 },
      { time: '12:00', line: 135 }, { time: '13:00', line: 131 }, { time: '14:00', line: 128 },
    ],
  },
];

const HEATMAP_DATA = [
  { game: 'BOS-TOR', moneyline: 85, total: 42, puckline: 68 },
  { game: 'COL-VGK', moneyline: 45, total: 78, puckline: 55 },
  { game: 'EDM-CGY', moneyline: 32, total: 28, puckline: 22 },
  { game: 'NYR-PHI', moneyline: 62, total: 35, puckline: 88 },
  { game: 'TBL-FLA', moneyline: 55, total: 65, puckline: 45 },
];

function getHeatColor(val: number): string {
  if (val >= 70) return '#ef4444';
  if (val >= 50) return '#f59e0b';
  if (val >= 30) return '#22c55e';
  return '#374151';
}

export default function MarketIntelligenceClient() {
  const [selected, setSelected] = useState<LineMovement>(MOVEMENTS[0]);
  const [filter, setFilter] = useState<'all' | 'sharp' | 'anomaly'>('all');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(interval);
  }, []);

  const filtered = MOVEMENTS.filter(m => {
    if (filter === 'sharp') return m.signal === 'sharp';
    if (filter === 'anomaly') return m.anomaly;
    return true;
  });

  const SIGNAL_COLOR = (s: string) => s === 'sharp' ? 'text-negative' : s === 'public' ? 'text-positive' : 'text-muted-foreground';
  const VELOCITY_COLOR = (v: string) => v === 'fast' ? 'text-negative' : v === 'moderate' ? 'text-yellow-400' : 'text-positive';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 flex items-center justify-center">
            <Icon name="ChartBarIcon" size={20} className="text-orange-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Market Intelligence</h1>
            <p className="text-xs text-muted-foreground font-mono-data">Line Movements · Sharp/Public · Sportsbook Divergences</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-positive-subtle border border-positive/20">
            <span className="w-1.5 h-1.5 rounded-full bg-positive pulse-dot" />
            <span className="text-2xs font-mono-data text-positive">LIVE MARKET</span>
          </div>
          {['all', 'sharp', 'anomaly'].map(f => (
            <button key={f} onClick={() => setFilter(f as typeof filter)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f ? 'bg-primary text-primary-foreground' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}>
              {f === 'all' ? 'Tous' : f === 'sharp' ? '⚡ Sharp' : '🔴 Anomalies'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Movement list */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">Mouvements Détectés</h3>
          {filtered.map(m => (
            <button key={m.id} onClick={() => setSelected(m)}
              className={`w-full text-left p-3 rounded-xl border transition-all ${selected.id === m.id ? 'bg-primary/10 border-primary/40' : 'bg-card border-border hover:border-primary/20'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-foreground">{m.game}</span>
                {m.anomaly && <span className="text-2xs px-1.5 py-0.5 rounded bg-negative-subtle text-negative font-mono-data">ANOMALIE</span>}
              </div>
              <div className="text-xs text-muted-foreground mb-2">{m.market}</div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono-data text-muted-foreground">{m.openLine > 0 ? '+' : ''}{m.openLine}</span>
                  <Icon name="ArrowRightIcon" size={10} className="text-muted-foreground" />
                  <span className={`text-xs font-mono-data font-bold ${m.movement < 0 ? 'text-negative' : 'text-positive'}`}>
                    {m.currentLine > 0 ? '+' : ''}{m.currentLine}
                  </span>
                </div>
                <span className={`text-xs font-semibold ${SIGNAL_COLOR(m.signal)}`}>
                  {m.signal === 'sharp' ? '⚡ SHARP' : '👥 PUBLIC'}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2 space-y-4">
          {/* Line timeline */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">{selected.game} — {selected.market}</h3>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-mono-data ${VELOCITY_COLOR(selected.velocity)}`}>
                  {selected.velocity === 'fast' ? '🔴 RAPIDE' : selected.velocity === 'moderate' ? '🟡 MODÉRÉ' : '🟢 LENT'}
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={selected.timeline}>
                <defs>
                  <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00D4FF" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#00D4FF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#888' }} />
                <YAxis tick={{ fontSize: 10, fill: '#888' }} domain={['auto', 'auto']} />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }} />
                <Area type="monotone" dataKey="line" stroke="#00D4FF" fill="url(#lineGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Sharp vs Public */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Sharp / Public Split</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs text-negative font-medium">⚡ Sharp Money</span>
                    <span className="text-xs font-mono-data text-negative">{selected.sharpPct}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div className="h-3 rounded-full bg-negative transition-all" style={{ width: `${selected.sharpPct}%` }} />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs text-positive font-medium">👥 Public Money</span>
                    <span className="text-xs font-mono-data text-positive">{selected.publicPct}%</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div className="h-3 rounded-full bg-positive transition-all" style={{ width: `${selected.publicPct}%` }} />
                  </div>
                </div>
                <div className="pt-2 border-t border-border">
                  <div className="flex justify-between">
                    <span className="text-xs text-muted-foreground">Signal dominant:</span>
                    <span className={`text-xs font-bold ${SIGNAL_COLOR(selected.signal)}`}>
                      {selected.signal === 'sharp' ? '⚡ SHARP ACTION' : '👥 PUBLIC ACTION'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Book consensus */}
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Consensus Sportsbooks</h3>
              <div className="space-y-2">
                {selected.books.map((book, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-background">
                    <span className="text-xs text-muted-foreground">{book.name}</span>
                    <span className={`text-xs font-mono-data font-bold ${book.line === Math.min(...selected.books.map(b => b.line)) ? 'text-negative' : book.line === Math.max(...selected.books.map(b => b.line)) ? 'text-positive' : 'text-foreground'}`}>
                      {book.line > 0 ? '+' : ''}{book.line}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Volatility heatmap */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="FireIcon" size={14} className="text-orange-400" />
              Heatmap Volatilité Marché
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left text-muted-foreground font-mono-data py-1 pr-4">MATCH</th>
                    <th className="text-center text-muted-foreground font-mono-data py-1 px-3">MONEYLINE</th>
                    <th className="text-center text-muted-foreground font-mono-data py-1 px-3">TOTAL</th>
                    <th className="text-center text-muted-foreground font-mono-data py-1 px-3">PUCK LINE</th>
                  </tr>
                </thead>
                <tbody>
                  {HEATMAP_DATA.map((row, i) => (
                    <tr key={i}>
                      <td className="text-foreground font-medium py-1.5 pr-4">{row.game}</td>
                      {(['moneyline', 'total', 'puckline'] as const).map(col => (
                        <td key={col} className="text-center py-1.5 px-3">
                          <span className="inline-block px-3 py-1 rounded-lg text-white text-2xs font-mono-data font-bold"
                            style={{ backgroundColor: getHeatColor(row[col]) }}>
                            {row[col]}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
              {[{ color: '#374151', label: '< 30 Faible' }, { color: '#22c55e', label: '30-50 Modéré' }, { color: '#f59e0b', label: '50-70 Élevé' }, { color: '#ef4444', label: '> 70 Critique' }].map((l, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded" style={{ backgroundColor: l.color }} />
                  <span className="text-2xs text-muted-foreground">{l.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
